import React from "react";
import {
  Home, Briefcase, ShieldCheck, Wallet, Scale, HeartPulse, TrendingUp, PiggyBank, Building2, Landmark,
  Mic, MessageSquare, CalendarCheck, Bot,
  Database, GitBranch, Workflow, FileBarChart2,
} from "lucide-react";

/**
 * OrbitalEcosystem v2 — cal.com-style PERFECTLY DISTRIBUTED orbital animation.
 *
 *  Implementation (the fix):
 *    For each orbit, an absolutely-positioned circular container fills the
 *    orbit radius. Each ICON sits on a "slot-arm" that ALSO fills the orbit
 *    (inset:0) and is rotated by the slot's fixed angle. The icon sits at the
 *    TOP-CENTER of the arm — so rotating the arm by N degrees places the icon
 *    N degrees around the orbit rim.
 *
 *    Animation: the orbit CONTAINER rotates continuously. To keep icons upright,
 *    each icon is wrapped in TWO counter-rotation divs:
 *      - one static `rotate(-slotAngle)`  → undoes the slot arm
 *      - one CSS animation               → undoes the orbit container rotation
 *
 *  Result: icons distribute evenly around all three orbits and stay upright.
 */

const OUTER = [
  { Icon: Home,        label: "Mortgages" },
  { Icon: Briefcase,   label: "Business Finance" },
  { Icon: ShieldCheck, label: "Debt Help" },
  { Icon: Wallet,      label: "Payday Loans" },
  { Icon: Scale,       label: "Claims" },
  { Icon: HeartPulse,  label: "Life Insurance" },
  { Icon: TrendingUp,  label: "Investor" },
  { Icon: PiggyBank,   label: "Wealth" },
  { Icon: Building2,   label: "Commercial" },
  { Icon: Landmark,    label: "Property" },
];

const MIDDLE = [
  { Icon: Mic,           label: "Voice" },
  { Icon: MessageSquare, label: "Chat" },
  { Icon: Bot,           label: "WhatsApp" },
  { Icon: CalendarCheck, label: "Bookings" },
];

const INNER = [
  { Icon: Database,      label: "Ping Tree" },
  { Icon: GitBranch,     label: "Routing" },
  { Icon: Workflow,      label: "CRM" },
  { Icon: FileBarChart2, label: "Reporting" },
];

export default function OrbitalEcosystem() {
  return (
      <div className="relative w-full aspect-square max-w-[640px] mx-auto select-none" data-testid="orbital-ecosystem">
      <style>{`
        @keyframes orbitCW   { from { transform: translate(-50%, -50%) rotate(0deg);   } to { transform: translate(-50%, -50%) rotate(360deg);  } }
        @keyframes orbitCCW  { from { transform: translate(-50%, -50%) rotate(0deg);   } to { transform: translate(-50%, -50%) rotate(-360deg); } }
        @keyframes counterCW  { from { transform: rotate(0deg);  } to { transform: rotate(-360deg); } }
        @keyframes counterCCW { from { transform: rotate(0deg);  } to { transform: rotate(360deg);  } }
        @keyframes hubPulse {
          0%, 100% { box-shadow: 0 0 30px rgba(0,85,255,0.5), 0 0 80px rgba(0,85,255,0.25); }
          50%      { box-shadow: 0 0 50px rgba(0,85,255,0.85), 0 0 140px rgba(0,85,255,0.4); }
        }
        .orb-outer  { animation: orbitCW   80s linear infinite; }
        .orb-middle { animation: orbitCCW  55s linear infinite; }
        .orb-inner  { animation: orbitCW   35s linear infinite; }
        .ctr-outer  { animation: counterCW  80s linear infinite; }
        .ctr-middle { animation: counterCCW 55s linear infinite; }
        .ctr-inner  { animation: counterCW  35s linear infinite; }
        .hub        { animation: hubPulse 2.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .orb-outer, .orb-middle, .orb-inner, .ctr-outer, .ctr-middle, .ctr-inner, .hub { animation: none; }
        }
      `}</style>

      <Orbit sizePct={94} animClass="orb-outer"  counterClass="ctr-outer"  items={OUTER}  tone="slate" size="lg" />
      <Orbit sizePct={68} animClass="orb-middle" counterClass="ctr-middle" items={MIDDLE} tone="brand" size="xs" />
      <Orbit sizePct={44} animClass="orb-inner"  counterClass="ctr-inner"  items={INNER}  tone="dark"  size="xs" />

      {/* CENTER HUB — Connexis mark */}
      <div className="absolute inset-0 grid place-items-center pointer-events-none z-10">
        <span className="hub inline-grid place-items-center size-20 sm:size-24 rounded-2xl bg-[#0a1124] ring-1 ring-white/15">
          <svg viewBox="0 0 24 24" className="size-9 sm:size-10" fill="none" stroke="currentColor" strokeWidth="2.1">
            <path d="M5 7h7M5 12h12M5 17h7" strokeLinecap="round" stroke="#ffffff" />
            <circle cx="18" cy="7"  r="2.4" fill="#3d7bff" stroke="none" />
            <circle cx="18" cy="17" r="2.4" fill="#3d7bff" stroke="none" />
          </svg>
        </span>
      </div>
      </div>
  );
}

function Orbit({ sizePct, animClass, counterClass, items, tone, size }) {
  const N = items.length;

  return (
    <div
      className={`absolute top-1/2 left-1/2 ${animClass}`}
      style={{
        width: `${sizePct}%`,
        height: `${sizePct}%`,
        // The transform here is the STARTING state — the keyframe overrides it.
        transform: "translate(-50%, -50%)",
      }}
    >
      {/* Visible dashed orbit ring (stays inside the rotating container so it
          aligns perfectly; rotation of a circle is visually identical to no
          rotation, so this is safe) */}
      <div className="absolute inset-0 rounded-full border border-dashed border-slate-300/55 pointer-events-none" aria-hidden />

      {items.map((it, i) => {
        const slot = (i * 360) / N;
        return (
          <div
            key={i}
            className="absolute inset-0 pointer-events-none"
            style={{ transform: `rotate(${slot}deg)` }}
          >
            {/* Icon sits at TOP-CENTER of the slot arm (which fills the orbit).
                Then we counter-rotate twice to keep it upright. Every wrapper
                must be pointer-events-none — otherwise rotating wrappers (whose
                bounding boxes are larger than the icon) catch hovers from
                neighbouring slots. Pointer-events become "auto" again only on
                the final visible icon span. */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              <div className="pointer-events-none" style={{ transform: `rotate(${-slot}deg)` }}>
                <div className={`${counterClass} pointer-events-none`}>
                  <OrbitIcon Icon={it.Icon} label={it.label} tone={tone} size={size} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function OrbitIcon({ Icon, label, tone = "slate", size = "lg" }) {
  const sizing = size === "xs"
    ? "size-9  rounded-lg [&_svg]:size-4"
    : size === "sm"
    ? "size-10 rounded-lg [&_svg]:size-[18px]"
    : size === "md"
    ? "size-11 rounded-xl [&_svg]:size-[18px]"
    : "size-12 rounded-xl [&_svg]:size-5";

  const toneCls = {
    slate: "bg-white border border-slate-200 text-slate-700 shadow-soft",
    brand: "bg-[#0055FF] text-white shadow-[0_8px_24px_rgba(0,85,255,0.4)]",
    dark:  "bg-slate-900 text-white shadow-md",
  }[tone];

  // CSS-only hover: wrap in a group; label is a peer that shows when icon
  // is hovered. Pure CSS — no framework state, fires reliably on every icon
  // regardless of how many siblings are present or how they're transformed.
  return (
    <div className="relative inline-block group/orbit pointer-events-auto">
      <span className={`inline-grid place-items-center ${sizing} ${toneCls} cursor-pointer transition-transform group-hover/orbit:scale-110`}>
        <Icon strokeWidth={1.7} />
      </span>
      <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-2 -translate-y-full whitespace-nowrap rounded-md bg-slate-900 text-white text-[10.5px] font-medium tracking-wide px-2 py-1 opacity-0 group-hover/orbit:opacity-100 transition-opacity z-[60] shadow-lg">
        {label}
      </span>
    </div>
  );
}
